import React, { useState, useEffect } from "react";
import { Button, Label, TextInput, Select, Alert, Spinner, Table } from "flowbite-react";
import { HiInformationCircle } from "react-icons/hi";
import { uploadTradeData, auth } from "../firebase";
import { useCapitalCredentials } from "../hooks/useCapitalCredentials";
import { capitalApiClient } from "../api/CapitalApiClient";

const API_URLS = {
  LIVE: "https://api-capital.backend-capital.com/api/v1",
  DEMO: "https://demo-api-capital.backend-capital.com/api/v1",
};

const ImportCapital = () => {
  const {
    apiKey,
    setApiKey,
    identifier,
    setIdentifier,
    password,
    setPassword,
    selectedApiUrl,
    setSelectedApiUrl,
    loading: credentialsLoading,
    error: credentialsError,
    saveCredentials,
  } = useCapitalCredentials();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [fromTime, setFromTime] = useState("00:00");
  const [toTime, setToTime] = useState("23:59");
  const [type, setType] = useState("TRADE");
  const [editMode, setEditMode] = useState(false);
  const [fetchedTrades, setFetchedTrades] = useState([]);

  const user = auth.currentUser;
  const userId = user?.uid;

  useEffect(() => {
    if (apiKey && identifier && password && selectedApiUrl) {
      capitalApiClient.init(apiKey, identifier, password, selectedApiUrl);
    }
  }, [apiKey, identifier, password, selectedApiUrl]);

  const setDateRange = (range) => {
    const now = new Date();
    let from = new Date();
    let to = new Date();

    to.setHours(to.getHours() - 1);
    to.setMinutes(0, 0, 0);

    const formattedTime = now.toTimeString().slice(0, 5);
    setToTime(formattedTime);

    switch (range) {
      case "today":
        setFromDate(now.toISOString().split("T")[0]);
        setToDate(now.toISOString().split("T")[0]);
        setFromTime("00:00");
        setToTime(to.toTimeString().slice(0, 5));
        break;
      case "yesterday":
        from.setDate(now.getDate() - 1);
        setFromDate(from.toISOString().split("T")[0]);
        setToDate(from.toISOString().split("T")[0]);
        setFromTime("00:00");
        setToTime("23:59");
        break;
      case "week":
        from.setDate(now.getDate() - 7);
        setFromDate(from.toISOString().split("T")[0]);
        setToDate(now.toISOString().split("T")[0]);
        setFromTime("00:00");
        setToTime(to.toTimeString().slice(0, 5));
        break;
      case "month":
        from.setMonth(now.getMonth() - 1);
        setFromDate(from.toISOString().split("T")[0]);
        setToDate(now.toISOString().split("T")[0]);
        setFromTime("00:00");
        setToTime(to.toTimeString().slice(0, 5));
        break;
      case "previousMonth":
        const currentDate = new Date();
        currentDate.setDate(0);
        const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const lastDay = new Date(currentDate);
        setFromDate(firstDay.toISOString().split("T")[0]);
        setToDate(lastDay.toISOString().split("T")[0]);
        setFromTime("00:00");
        setToTime("23:59");
        break;
    }
  };

  const transformCapitalData = (capitalData) => {
    return capitalData.map((transaction) => {
      const tradeIdParts = transaction.reference.includes("_") ? transaction.reference.split("_") : [];
      const secondPartOfTradeId = tradeIdParts.length > 1 ? tradeIdParts[1] : transaction.reference;

      return {
        Id: secondPartOfTradeId,
        Modified: transaction.date,
        Balance: "0",
        Amount: transaction.size,
        Currency: transaction.currency,
        Type: transaction.transactionType,
        Status: transaction.status,
        "Trade Id": transaction.dealId,
        "Instrument Symbol": transaction.instrumentName,
        "Instrument Name": transaction.instrumentName,
        Commission: "0",
        "Account type": "CAPITAL",
      };
    });
  };

  const handleUpload = async () => {
    setLoading(true);
    setError(null);
    try {
      const uploadResult = await uploadTradeData(userId, fetchedTrades);
      setResult(uploadResult);
      setFetchedTrades([]);
    } catch (err) {
      console.error("Error uploading data:", err);
      setError("Error uploading data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTradeHistory = async () => {
    setLoading(true);
    setError(null);
    setFetchedTrades([]);

    try {
      console.log("Fetching trade history");

      const response = await capitalApiClient.request(
        "GET",
        `/history/transactions?from=${fromDate}T${fromTime}&to=${toDate}T${toTime}&lastPeriod=600&type=${type}`
      );

      const capitalData = response.transactions || [];
      if (capitalData.length <= 0) {
        setError("No data!");
        return;
      }

      const transformedData = transformCapitalData(capitalData);
      setFetchedTrades(transformedData); // Set the fetched trades
    } catch (err) {
      console.error("Error details:", err);
      if (err.response && err.response.status === 401) {
        setError("Authentication failed. Please check your API credentials.");
      } else if (err.response && err.response.status === 503) {
        setError("The Capital.com service is currently unavailable. Please try again later.");
      } else {
        setError(err.response?.data?.errorMessage || err.message || "An error occurred while fetching trade history");
      }
    } finally {
      setLoading(false);
    }
  };

  const additionalContent = (
    <>
      <ol className="list-decimal list-inside">
        <li>Log in to your Capital.com account</li>
        <li>Go to Settings > API integrations</li>
        <li>Click on "Generate API key"</li>
        <li>Follow the prompts to set up 2FA if needed</li>
        <li>Label the API 'Tradewise', create a new password for the API</li>
        <li>Save the API Key securely as it's shown only once</li>
      </ol>
      <img alt="" src="/images/capitalAPI.png" className="mt-5 rounded-xl w-1/2" />
    </>
  );

  if (credentialsLoading) {
    return <Spinner aria-label="Loading credentials" />;
  }

  if (credentialsError) {
    return <Alert color="failure">{credentialsError}</Alert>;
  }

  return (
    <main className="fh-full overflow-y-auto bg-slate-100 dark:bg-slate-900 text-slate-400 ">
      <div className=" p-4 flex-grow">
        <h2 className="text-2xl font-bold mb-4">Import Capital.com Trades</h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 ">
          {/* Date Selection Section */}
          <div className="h-full bg-white dark:bg-slate-800 text-slate-400 p-4 rounded-lg">
            <h3 className="text-xl mb-4">Select Date Range</h3>

            <div className="mb-4">
              <div className="mb-2 block">
                <Label htmlFor="apiUrlSelect">API Environment</Label>
              </div>
              <Select id="apiUrlSelect" value={selectedApiUrl} onChange={(e) => setSelectedApiUrl(e.target.value)}>
                <option value={API_URLS.LIVE}>Live</option>
                <option value={API_URLS.DEMO}>Demo</option>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {["today", "yesterday", "week", "month", "previousMonth"].map((range) => (
                <Button
                  key={range}
                  size="md"
                  onClick={() => setDateRange(range)}
                  className="bg-blue-500 text-white py-2 px-4 rounded-lg mb-4 flex items-center justify-center"
                >
                  {range.charAt(0).toUpperCase() + range.slice(1)}
                </Button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="mb-2 block">
                  <Label htmlFor="fromDate">From Date</Label>
                </div>
                <TextInput sizing="md" id="fromDate" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </div>
              <div>
                <div className="mb-2 block">
                  <Label htmlFor="toDate">To Date</Label>
                </div>
                <TextInput sizing="md" id="toDate" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </div>
            </div>
            <div className="mb-4">
              <div className="mb-2 block">
                <Label htmlFor="type">Type</Label>
              </div>
              <Select sizing="md" id="type" value={type} onChange={(e) => setType(e.target.value)}>
                <option value="TRADE">Trade</option>
                <option value="DEPOSIT">Deposit</option>
                <option value="SWAP">Swap</option>
              </Select>
            </div>
            <Button
              onClick={fetchTradeHistory}
              className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg mb-4 flex items-center justify-center"
            >
              View Trades
            </Button>
            <Button
              onClick={handleUpload}
              disabled={fetchedTrades.length === 0 || loading}
              isProcessing={loading}
              className="w-full bg-green-500 text-white py-2 px-4 rounded-lg mb-4 flex items-center justify-center"
            >
              {loading ? "Importing..." : "Import Trades"}
            </Button>
            {error && (
              <Alert color="failure" icon={HiInformationCircle} className="mt-4">
                <span className="font-medium">Error!</span> {error}
              </Alert>
            )}
            {result && (
              <Alert color="success" icon={HiInformationCircle} className="mt-4">
                <span className="font-medium">Success!</span> Added: {result.added}, Skipped: {result.skipped}
              </Alert>
            )}
          </div>

          {/* API Credentials Section */}
          <div className="h-full bg-white dark:bg-slate-800 text-slate-400 p-4 rounded-lg">
            <h3 className="text-xl mb-4">API Credentials</h3>
            {!editMode ? (
              <div className="dark:text-white text-black">
                <p>API Key: {apiKey}</p>
                <p>Identifier: {identifier}</p>
                <p>Password: ••••••••</p>
                <Button
                  onClick={() => setEditMode(true)}
                  className="bg-blue-500 text-white py-2 px-4 rounded-lg my-4 flex items-center justify-center"
                >
                  Edit
                </Button>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <div className="mb-2 block">
                    <Label htmlFor="apiKey">API Key</Label>
                  </div>
                  <TextInput
                    sizing="md"
                    id="apiKey"
                    type="text"
                    placeholder="API Key"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <div className="mb-2 block">
                    <Label htmlFor="identifier">Identifier (Email)</Label>
                  </div>
                  <TextInput
                    sizing="md"
                    id="identifier"
                    type="text"
                    placeholder="Identifier (Email)"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <div className="mb-2 block">
                    <Label htmlFor="password">Password</Label>
                  </div>
                  <TextInput
                    sizing="md"
                    id="password"
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={saveCredentials}
                    className="w-full bg-green-500 text-white py-2 px-4 rounded-lg mb-4 flex items-center justify-center"
                  >
                    Save API Credentials
                  </Button>
                  <Button
                    color="failure"
                    onClick={() => setEditMode(false)}
                    className="w-full bg-slate-500 text-white py-2 px-4 rounded-lg mb-4 flex items-center justify-center"
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
            <Alert color="info" icon={HiInformationCircle} size={50} className="mt-4" additionalContent={additionalContent}>
              <span className="font-medium">How to generate Capital.com API Key</span>
            </Alert>
          </div>
        </div>
        <div className="mb-5">
          {fetchedTrades.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <div className="flex justify-between">
                <h4 className="text-lg font-semibold mb-2">Fetched Trades</h4>
                {fetchedTrades.length > 10 && (
                  <p className="mt-2 text-sm text-gray-500">Showing first 10 trades of {fetchedTrades.length} total trades</p>
                )}
              </div>
              <Table striped>
                <Table.Head>
                  <Table.HeadCell>ID</Table.HeadCell>
                  <Table.HeadCell>Date</Table.HeadCell>
                  <Table.HeadCell>Amount</Table.HeadCell>
                  <Table.HeadCell>Instrument</Table.HeadCell>
                  <Table.HeadCell>Type</Table.HeadCell>
                </Table.Head>
                <Table.Body>
                  {fetchedTrades.slice(0, 10).map((trade, index) => (
                    <Table.Row key={index}>
                      <Table.Cell>{trade.Id}</Table.Cell>
                      <Table.Cell>{trade.Modified}</Table.Cell>
                      <Table.Cell>{trade.Amount}</Table.Cell>
                      <Table.Cell>{trade["Instrument Symbol"]}</Table.Cell>
                      <Table.Cell>{trade.Type}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default ImportCapital;
